"use client";

import { useEffect, useState } from "react";

/** A single pending change item shown in the modal. */
export type ChangeItem = {
  id: string;
  /** Section grouping label (e.g. "Organization Details"). */
  section: string;
  /** Human-readable description of the change. */
  description: string;
};

export interface ChangesModalProps {
  /** Pending change items to display. */
  changes: ChangeItem[];
  /** Called when the user confirms reverting all changes. */
  onRevert: () => void;
  /** Called when the user dismisses the modal (Escape / overlay click / Done). */
  onClose: () => void;
}

/* ── Shared class strings ─────────────────────────────────────────────── */

const CLS_BTN = [
  "inline-flex items-center justify-center",
  "h-8 px-[14px]",
  "rounded-[var(--radius-field)]",
  "text-sm font-medium",
  "cursor-pointer",
  "transition-[background,color,border-color] duration-[120ms]",
].join(" ");

/**
 * Modal dialog listing unsaved changes with a two-step "Revert all" confirm
 * flow. Purely presentational — all mutations are forwarded via callbacks.
 *
 * Pressing Escape or clicking the backdrop dismisses without reverting.
 */
export function ChangesModal({
  changes,
  onRevert,
  onClose,
}: ChangesModalProps) {
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  function doRevert() {
    onRevert();
    onClose();
  }

  // Group changes by section for compact display.
  const grouped = new Map<string, ChangeItem[]>();
  for (const c of changes) {
    const list = grouped.get(c.section) ?? [];
    list.push(c);
    grouped.set(c.section, list);
  }

  return (
    <div
      className={[
        "fixed inset-0 z-100",
        "flex items-center justify-center p-6",
        "bg-[color-mix(in_oklab,var(--brand-navy)_30%,transparent)]",
      ].join(" ")}
      role="dialog"
      aria-modal="true"
      aria-label="Unsaved changes"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
    >
      <div
        role="document"
        className={[
          "w-full max-w-135 max-h-[80vh]",
          "bg-(--bg-white)",
          "rounded-(--radius-container)",
          "shadow-(--shadow-lg)",
          "flex flex-col overflow-hidden",
        ].join(" ")}
        onKeyDown={(e) => {
          if (e.key !== "Escape") e.stopPropagation();
        }}
      >
        {/* ── Header ─────────────────────────────────────────────────── */}
        <header
          className={[
            "flex items-start justify-between gap-4",
            "px-5 pt-4.5 pb-3.5",
            "border-b border-(--border-subtle)",
          ].join(" ")}
        >
          <div>
            <h2 className="font-(--font-display) text-[14px] tracking-[-0.015em] text-(--fg-primary) m-0">
              Unsaved changes
            </h2>
            <p className="text-[12.5px] text-(--fg-muted) mt-1 mb-0">
              {changes.length} change{changes.length === 1 ? "" : "s"} since
              last save
            </p>
          </div>

          <button
            type="button"
            className={[
              "inline-flex items-center justify-center",
              "w-7 h-7 rounded-[3px]",
              "text-(--fg-muted)",
              "cursor-pointer",
              "transition-[background,color] duration-100",
              "hover:bg-(--bg-hover) hover:text-(--fg-primary)",
            ].join(" ")}
            onClick={onClose}
            aria-label="Close"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden={true}
            >
              <path d="M6 6l12 12M18 6l-12 12" />
            </svg>
          </button>
        </header>

        {/* ── Body ───────────────────────────────────────────────────── */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 pt-1.5 pb-3.5">
          {changes.length === 0 ? (
            <p className="my-6 text-center text-[13px] text-(--fg-muted)">
              No changes to show.
            </p>
          ) : (
            [...grouped.entries()].map(([section, list]) => (
              <section
                key={section}
                className="py-3.5 border-b border-(--border-subtle) last:border-b-0"
              >
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-(--fg-muted) m-0 mb-2">
                  {section}
                </h3>
                <ul className="list-none p-0 m-0 flex flex-col gap-1">
                  {list.map((c) => (
                    <li
                      key={c.id}
                      className="flex items-start gap-2.5 text-[13px] leading-normal text-(--fg-primary)"
                    >
                      {/* Bullet dot replacing ::before pseudo-element */}
                      <span
                        className="mt-2.25 w-1 h-1 rounded-[3px] bg-(--fg-subtle) shrink-0"
                        aria-hidden
                      />
                      {c.description}
                    </li>
                  ))}
                </ul>
              </section>
            ))
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────────────── */}
        <footer
          className={[
            "flex items-center justify-between gap-3",
            "px-5 py-3.5",
            "border-t border-(--border-subtle)",
            "bg-(--bg-white)",
          ].join(" ")}
        >
          {confirming ? (
            <>
              <span className="text-[12.5px] text-(--fg-secondary) flex-1 min-w-0">
                Discard {changes.length} change{changes.length === 1 ? "" : "s"}
                ? This cannot be undone.
              </span>
              <div className="inline-flex gap-2">
                <button
                  type="button"
                  className={`${CLS_BTN} bg-(--bg-canvas) text-(--fg-secondary) hover:bg-(--bg-focused) hover:text-(--fg-primary)`}
                  onClick={() => setConfirming(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`${CLS_BTN} bg-(--status-error) text-(--fg-inverse) hover:bg-(--error-700)`}
                  onClick={doRevert}
                >
                  Revert all
                </button>
              </div>
            </>
          ) : (
            <>
              <button
                type="button"
                className={[
                  CLS_BTN,
                  "text-(--status-error-text) bg-transparent",
                  "hover:enabled:bg-(--status-error-bg)",
                  "disabled:text-(--fg-subtle) disabled:cursor-default",
                ].join(" ")}
                onClick={() => setConfirming(true)}
                disabled={changes.length === 0}
              >
                Revert all
              </button>
              <button
                type="button"
                className={`${CLS_BTN} bg-(--brand-navy) text-(--fg-inverse) hover:bg-(--steel-110)`}
                onClick={onClose}
              >
                Done
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}
